import React from "react";
import {
  NumericFormat,
  type NumberFormatValues,
} from "react-number-format";
import clsx from "clsx";

type InputNumberProps = {
  label?: string;
  hint?: string;
  error?: boolean;
  success?: boolean;
  className?: string;
  containerClassName?: string;
  onValueChange?: (values: NumberFormatValues) => void;
} & Omit<
  React.ComponentPropsWithoutRef<typeof NumericFormat>,
  "onValueChange"
>;

const baseInputClass =
  "h-11 w-full rounded-lg border border-gray-200 appearance-none px-4 py-2.5 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-300 bg-white text-gray-900 dark:bg-white dark:text-gray-900 dark:placeholder:text-gray-400";

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      label,
      hint,
      error = false,
      success = false,
      className,
      containerClassName,
      thousandSeparator = ".",
      decimalSeparator = ",",
      allowNegative = false,
      suffix = "",
      ...rest
    },
    ref,
  ) => {
    const inputClasses = clsx(baseInputClass, className, {
      "text-gray-500 border-gray-200 opacity-60 bg-gray-100 cursor-not-allowed dark:bg-gray-200 dark:text-gray-500 dark:border-gray-200":
        rest.disabled,
      "border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:text-error-400 dark:border-error-500 dark:focus:border-error-800":
        error && !rest.disabled,
      "border-success-500 focus:border-success-300 focus:ring-success-500/20 dark:text-success-400 dark:border-success-500 dark:focus:border-success-800":
        success && !rest.disabled,
      "bg-white text-gray-900 border-gray-200 focus:border-brand-300 focus:ring-brand-500/20":
        !error && !success && !rest.disabled,
    });

    return (
      <div className={clsx("space-y-1", containerClassName)}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 dark:text-white/80">
            {label}
          </label>
        )}
        <NumericFormat
          {...rest}
          getInputRef={ref}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          allowNegative={allowNegative}
          suffix={suffix}
          className={inputClasses}
          inputMode="decimal"
        />
        {hint && (
          <p
            className={clsx("text-xs", {
              "text-error-500": error,
              "text-success-500": success,
              "text-gray-500": !error && !success,
            })}
          >
            {hint}
          </p>
        )}
      </div>
    );
  },
);

InputNumber.displayName = "InputNumber";

export default InputNumber;
